// The pipeline as the screens read it (Part 2, 2026-10-19): every opportunity as one Lead with its stage, the step on
// now, its strip, its value (job by job), likelihood, quiet days, dates, what needs the Owner and the review link beside
// the step. One hook, every pipeline screen; the rules are the engine's (packages/engine/src/pipeline.ts). Writes go
// through the database functions that make a tap one step and leave the log entry with what Undo needs.

import { useMemo } from 'react';
import { supabase } from './supabase';
import {
  afterProposal,
  applyingSteps,
  contactHistory,
  daysBetween,
  daysLate,
  eventIsTrue,
  nextTouchArrived,
  isDateCondition,
  isQuiet,
  isRetired,
  leadFacts,
  leadValue,
  likelihoodFor,
  lostNextTouch,
  needsOwner,
  nextStep,
  outcomeRefusal,
  parkedReturn,
  quietDays,
  reviewLinkFor,
  STEP,
  stepListFor,
  stepOnNow,
  stepPosition,
  workingStages,
  type LeadValue,
  type NextStep,
  type OpportunityStepRow,
  type PipelineCondition,
  type PipelineStage,
  type PipelineStep,
  type QuietSetting,
  type ReviewLink,
  type StepListRow,
  type StepPosition,
} from '@zerogap/engine';
import { fetchRows, insertRow, refresh, rpc, toast, updateRow, useRows, type Row } from './api';
import { contactName, today } from './format';
import { useAllJobs, type JobSummary } from './jobs';
import { useSession } from './session';

/** The answers a step records or a condition reads, with their words (the pipeline_answers setting). */
export interface AnswerWords {
  first_visit: { key: string; label: string }[];
  design_lane: { key: string; label: string }[];
  flags: { key: string; label: string }[];
}

export interface Lead {
  opp: Row;
  id: string;
  name: string;
  outcome: string;
  /** The working stage, or Won; null for a parked or lost lead not yet placed. */
  stage: PipelineStage | null;
  /** The step on now, read live: a date step that has come true lands ahead of the stored step (engine: stepOnNow); null until the lead is placed. */
  step: PipelineStep | null;
  /** The step on now is a date step that landed (Decide-by date: call it): it ends in Park or Lost. */
  dateStep: boolean;
  placed: boolean;
  rows: OpportunityStepRow[];
  doneIds: Set<string>;
  skippedIds: Set<string>;
  /** The lead's step list for its stage: done, skipped (with the words), on now, to do. */
  stepList: StepListRow[];
  /** The worked-out facts (2026-10-22): a new lead, reply-by passed. */
  newLead: boolean;
  replyByPassed: boolean;
  /** The primary contact's history on other leads, for the returning-client line. */
  history: { earlier: number; won: number; lost: number };
  /** A parked lead: where it comes back to (the stage and step it keeps); null when no step is on record. */
  returnTo: { stage: PipelineStage; step: PipelineStep } | null;
  /** The current step's ready row (Owner approves: the seat marked it ready), if any. */
  readyRow: OpportunityStepRow | null;
  position: StepPosition | null;
  value: LeadValue;
  jobs: JobSummary[];
  likelihood: number | null;
  quietDays: number | null;
  quiet: boolean;
  lastTouch: Row | null;
  /** YYYY-MM-DD, or null. */
  nextTouch: string | null;
  nextTouchLate: number | null;
  replyBy: string | null;
  replyLate: number | null;
  /** Reply-by or next touch passed (a parked lead: its re-approach date passed). */
  late: boolean;
  needsOwner: boolean;
  reviewLink: ReviewLink | null;
  daysOnStep: number | null;
  contacts: Row[];
  primary: Row | null;
  handoffs: Row[];
  afterProposal: boolean;
  /** Retired (decision 1, 2026-10-19): counts nowhere, on no board, in no value or win rate. Still opens by its link, marked. */
  retired: boolean;
}

export interface Pipeline {
  loading: boolean;
  stages: PipelineStage[];
  working: PipelineStage[];
  wonStage: PipelineStage | null;
  parkedStage: PipelineStage | null;
  lostStage: PipelineStage | null;
  seats: Row[];
  /** The Owner seat's id. */
  ownerSeat: string | null;
  /** Every step row, retired ones included (the steps tab shows them dimmed). */
  steps: PipelineStep[];
  conditions: PipelineCondition[];
  answers: AnswerWords;
  quietSetting: QuietSetting;
  /** The events a step may be done by (the pipeline_events setting). */
  events: { key: string; label: string }[];
  wonWindowDays: number;
  bands: number[];
  users: Row[];
  leads: Lead[];
  byId: Map<string, Lead>;
  /** Open leads with a stage. */
  open: Lead[];
  parked: Lead[];
  lost: Lead[];
  /** Won inside the window, newest first. */
  wonRecent: Lead[];
  wonAll: Lead[];
  seatOf: (step: PipelineStep | null | undefined) => { seat: Row | null; holder: Row | null };
  stageById: (id: string | null | undefined) => PipelineStage | null;
  stepById: (id: string | null | undefined) => PipelineStep | null;
  /** Where a lead goes when `from` is done, with an answer the step records counted in. */
  next: (lead: Lead, from: PipelineStep | null, answer?: Answer | null) => NextStep | null;
}

export interface Answer {
  first_visit?: string;
  design_lane?: string;
  flags?: Record<string, boolean>;
}

export const PIPELINE_TABLES = ['opportunity', 'opportunity_step', 'activity', 'pipeline_handoff', 'pipeline_stage', 'pipeline_seat', 'pipeline_step'] as const;

const dateOnly = (v: unknown): string | null => (v ? String(v).slice(0, 10) : null);

export function usePipeline(): Pipeline {
  const s = useSession();
  const stages = useRows('pipeline_stage', { order: ['sort'] });
  const seats = useRows('pipeline_seat', { order: ['sort'] });
  const steps = useRows('pipeline_step', { order: ['sort'] });
  const opps = useRows('opportunity', { order: ['created_at'] });
  const stepRows = useRows('opportunity_step', { order: ['at'] });
  const activity = useRows('activity', { order: ['-at'] });
  const handoffs = useRows('pipeline_handoff', { eq: { status: 'open' } });
  const contacts = useRows('contact');
  const links = useRows('opportunity_contact');
  const users = useRows('user', { order: ['created_at'] });
  const all = useAllJobs();
  const conditions = s.setting<PipelineCondition[]>('pipeline_conditions', []);
  const lostWeeks = s.setting<{ weeks?: number }>('pipeline_lost_retouch', {}).weeks ?? null;
  const answers = s.setting<AnswerWords>('pipeline_answers', { first_visit: [], design_lane: [], flags: [] });
  const quietSetting = s.setting<QuietSetting>('pipeline_quiet', { days: 0 });
  const events = s.setting<{ key: string; label: string }[]>('pipeline_events', []);
  const wonWindowDays = Number(s.setting<number>('won_window_days', 90)) || 90;
  const bands = s.setting<number[]>('job_size_bands', []);
  const loading = stages.loading || seats.loading || steps.loading || opps.loading || stepRows.loading || activity.loading || handoffs.loading || contacts.loading || links.loading || users.loading || all.loading;

  return useMemo(() => {
    const stageRows = stages.rows as PipelineStage[];
    const stepList = steps.rows as PipelineStep[];
    const working = workingStages(stageRows);
    const wonStage = stageRows.find((x) => x.kind === 'won') ?? null;
    const ownerSeat = (seats.rows.find((x) => x.key === 'owner')?.id as string | undefined) ?? null;
    const stageById = (id: string | null | undefined) => stageRows.find((x) => x.id === id) ?? null;
    const stepById = (id: string | null | undefined) => stepList.find((x) => x.id === id) ?? null;
    const seatOf = (step: PipelineStep | null | undefined) => {
      const seat = step ? seats.rows.find((x) => x.id === step.seat_id) ?? null : null;
      const holder = seat?.holder_user_id ? users.rows.find((u) => u.id === seat.holder_user_id) ?? null : null;
      return { seat, holder };
    };
    const now = Date.now();
    const td = today();
    const leads: Lead[] = opps.rows.map((opp) => {
      const rows = stepRows.rows.filter((r) => r.opportunity_id === opp.id) as OpportunityStepRow[];
      const stage = stageById(opp.pipeline_stage_id);
      const doneIds = new Set(rows.filter((r) => r.status === 'done' || r.status === 'skipped').map((r) => r.step_id));
      const skippedIds = new Set(rows.filter((r) => r.status === 'skipped').map((r) => r.step_id));
      // the facts a condition may read, worked out here and never stored (2026-10-22): a new lead, reply-by passed
      const history = contactHistory(opps.rows as never, links.rows as never, opp.primary_contact_id, opp as never);
      const everParked = opp.outcome === 'parked' || activity.rows.some((a) => a.opportunity_id === opp.id && a.kind === 'stage' && a.payload?.outcome === 'parked');
      const facts = leadFacts({ earlierOpportunities: history.earlier, everParked, replyBy: opp.reply_by, today: td });
      const answers = { ...opp, ...facts };
      const step = stepOnNow(stepList, conditions, answers, doneIds, opp.current_step_id);
      const dateStep = !!step && step.id !== opp.current_step_id && isDateCondition(conditions.find((c) => c.key === step.applies_when));
      const readyRow = step ? rows.find((r) => r.step_id === step.id && r.status === 'ready') ?? null : null;
      // a retired job counts nowhere: it is listed under the lead, marked, and adds nothing to its value (decision 1, 2026-10-19)
      const jobs = all.jobs.filter((j) => j.job.opportunity_id === opp.id);
      const value = leadValue(opp, jobs.filter((j) => !j.retired).map((j) => ({ price_cents: j.res ? j.res.totals.price_cents : null, unverified: j.res?.totals.unverified_items ?? 0 })));
      const lastTouch = activity.rows.find((a) => a.opportunity_id === opp.id && (a.kind === 'touch' || a.kind === 'concept_board')) ?? null;
      // days since the newest touch; a lead never touched counts from the day it was made (a fresh lead is not quiet on day one);
      // the threshold is the stage's own where the setting names it (the Landing Board's nudge days), else the one figure
      const qd = quietDays(lastTouch?.at ?? opp.created_at ?? null, now);
      const quiet = opp.outcome === 'open' && isQuiet(qd, quietSetting, stage?.key);
      const nextTouch = dateOnly(opp.next_touch_at);
      const replyBy = dateOnly(opp.reply_by);
      const nextTouchLate = nextTouch && nextTouch < td ? daysLate(nextTouch, now) : null;
      const replyLate = replyBy && replyBy < td ? daysLate(replyBy, now) : null;
      const linked = links.rows.filter((l) => l.opportunity_id === opp.id).map((l) => contacts.rows.find((c) => c.id === l.contact_id)).filter((c): c is Row => !!c);
      const primary = contacts.rows.find((c) => c.id === opp.primary_contact_id) ?? linked[0] ?? null;
      const contactList = primary ? [primary, ...linked.filter((c) => c.id !== primary.id)] : linked;
      const isOpen = opp.outcome === 'open';
      return {
        opp: answers,
        id: opp.id,
        name: String(opp.name),
        outcome: String(opp.outcome),
        stage,
        step,
        dateStep,
        placed: !!step,
        rows,
        doneIds,
        skippedIds,
        stepList: stage && stage.kind === 'working' ? stepListFor(stepList, stage.id, conditions, answers, doneIds, skippedIds, step?.id) : [],
        newLead: !!facts.new_lead,
        replyByPassed: !!facts.reply_by_passed,
        history,
        returnTo: opp.outcome === 'parked' ? parkedReturn(opp, stageRows, stepList) : null,
        readyRow,
        position: stage && stage.kind === 'working' ? stepPosition(stepList, stage.id, conditions, answers, doneIds, step?.id) : null,
        value,
        jobs,
        likelihood: likelihoodFor(opp, stageRows, quiet),
        quietDays: qd,
        quiet,
        lastTouch,
        nextTouch,
        nextTouchLate,
        replyBy,
        replyLate,
        // a parked or a lost lead whose re-approach date (its next touch) came due surfaces like a lead whose date passed (rule 4)
        late: (isOpen && (nextTouchLate !== null || replyLate !== null)) || ((opp.outcome === 'parked' || opp.outcome === 'lost') && nextTouchLate !== null),
        needsOwner: isOpen && needsOwner(step, ownerSeat, readyRow),
        reviewLink: reviewLinkFor(rows, stepList, step),
        daysOnStep: daysBetween(opp.current_step_since ?? null, now),
        contacts: contactList,
        primary,
        handoffs: handoffs.rows.filter((h) => h.opportunity_id === opp.id),
        afterProposal: afterProposal(rows, stepList, stageRows),
        retired: isRetired(opp),
      };
    });
    const byId = new Map(leads.map((l) => [l.id, l]));
    // a retired record counts nowhere (decision 1, 2026-10-19): off every list, count and value; it still opens by its link
    const counted = leads.filter((l) => !l.retired);
    const wonAll = counted.filter((l) => l.outcome === 'won').sort((a, b) => String(b.opp.outcome_at ?? '').localeCompare(String(a.opp.outcome_at ?? '')));
    const since = now - wonWindowDays * 86_400_000;
    return {
      loading,
      stages: stageRows,
      working,
      wonStage,
      parkedStage: stageRows.find((x) => x.kind === 'parked') ?? null,
      lostStage: stageRows.find((x) => x.kind === 'lost') ?? null,
      seats: seats.rows,
      ownerSeat,
      steps: stepList,
      conditions,
      answers,
      quietSetting,
      events,
      wonWindowDays,
      bands,
      users: users.rows,
      leads,
      byId,
      open: counted.filter((l) => l.outcome === 'open'),
      parked: counted.filter((l) => l.outcome === 'parked').sort((a, b) => String(a.nextTouch ?? '9').localeCompare(String(b.nextTouch ?? '9'))),
      lost: counted.filter((l) => l.outcome === 'lost'),
      wonRecent: wonAll.filter((l) => l.opp.outcome_at && new Date(l.opp.outcome_at).getTime() >= since),
      wonAll,
      seatOf,
      stageById,
      stepById,
      next: (lead, from, answer) => {
        const asked = {
          ...lead.opp,
          ...(answer?.first_visit ? { first_visit: answer.first_visit } : {}),
          ...(answer?.design_lane ? { design_lane: answer.design_lane } : {}),
          pipeline_flags: { ...(lead.opp.pipeline_flags ?? {}), ...(answer?.flags ?? {}) },
        };
        return nextStep(stepList, stageRows, conditions, asked, from);
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, stages.rows, seats.rows, steps.rows, opps.rows, stepRows.rows, activity.rows, handoffs.rows, contacts.rows, links.rows, users.rows, all.jobs, conditions, answers, quietSetting, events, wonWindowDays, bands, lostWeeks]);
}

/** The next touch a loss sets today, from the pipeline_lost_retouch setting (10 weeks by default); null when the setting says none. */
export function lostRetouch(weeks: number | null | undefined): string | null {
  return lostNextTouch(today(), weeks);
}

/** The steps of a lead's stage that apply to it, in order. */
export function applyingFor(p: Pipeline, lead: Lead): PipelineStep[] {
  return lead.stage ? applyingSteps(p.steps, lead.stage.id, p.conditions, lead.opp) : [];
}

/** The words for a seat on a step: "You" for the Owner seat, else "Designer · Rowan", "Client". */
export function seatWords(p: Pipeline, step: PipelineStep | null | undefined): { you: boolean; words: string } {
  const { seat, holder } = p.seatOf(step);
  if (!seat) return { you: false, words: '' };
  if (seat.id === p.ownerSeat) return { you: true, words: 'You' };
  return { you: false, words: holder ? `${seat.name} · ${holder.name}` : String(seat.name) };
}

/** What a step's done-rule says in a few words. */
export function doneWords(p: Pipeline, step: PipelineStep): string {
  switch (step.done_rule) {
    case 'seat_ticks':
      return p.seatOf(step).seat?.id === p.ownerSeat ? 'Owner ticks' : 'Seat ticks';
    case 'owner_approves':
      return 'Owner approves';
    case 'event':
      return `Event · ${p.events.find((e) => e.key === step.event_key)?.label ?? step.event_key ?? ''}`;
    case 'client_acts':
      return 'Client acts';
  }
}

/** The condition's words ("Free walkthrough only"), or "Every lead". */
export function conditionWords(p: Pipeline, step: PipelineStep): string {
  if (!step.applies_when) return 'Every lead';
  return p.conditions.find((c) => c.key === step.applies_when)?.label ?? step.applies_when;
}

export const PIPELINE_WRITE_TABLES = ['opportunity', 'opportunity_step', 'activity', 'pipeline_handoff', 'job', 'scope_row', 'opportunity_contact'];
const touched = () => refresh(...PIPELINE_WRITE_TABLES);

/** A tap's result: the log entry that can undo it, and the words the undo row shows. */
export interface Tap {
  entry: string;
  words: string;
  was: string;
}

/** Which round of a loop a step is on for this lead: one more than the rows already finished on it. */
export function roundFor(lead: Lead, step: PipelineStep): number {
  const finished = lead.rows.filter((r) => r.step_id === step.id && r.status !== 'ready');
  const open = lead.rows.find((r) => r.step_id === step.id && r.status === 'ready');
  return open ? open.round : finished.length + 1;
}

/**
 * A step marked done (or ready, or skipped) on a lead, the lead sent where the engine says, as one step in the database.
 * `to` overrides where the lead goes (the one loop: another pass sends it back to the step's back_to).
 */
export async function tickStep(
  p: Pipeline,
  lead: Lead,
  step: PipelineStep,
  status: 'done' | 'ready' | 'skipped',
  opts: { answer?: Answer | null; forUser?: string | null; link?: string | null; note?: string | null; to?: PipelineStep | null } = {},
): Promise<Tap> {
  const next = status === 'ready' ? null : opts.to ? { step: opts.to, stage: p.stageById(opts.to.stage_id)!, stageMoved: opts.to.stage_id !== step.stage_id } : p.next(lead, step, opts.answer);
  const finished = status !== 'ready' && !next;
  const entry = await rpc<string>('pipeline_tick', {
    p_opportunity: lead.id,
    p_step: step.id,
    p_status: status,
    p_next_step: next?.step.id ?? null,
    p_next_stage: next?.stage.id ?? null,
    p_round: roundFor(lead, step),
    p_review_link: opts.link ?? null,
    p_note: opts.note ?? null,
    p_for_user: opts.forUser ?? null,
    p_answer: opts.answer ?? null,
    p_outcome: finished ? 'won' : null,
  });
  touched();
  // an event step whose event is already true ticks as the lead lands on it (decision 4)
  const settled = status === 'ready' ? [] : await settleEvents(lead.id);
  const where = finished ? 'Won' : next ? `${next.stageMoved ? `${next.stage.name} · ` : ''}${next.step.name}` : step.name;
  const words = (status === 'ready' ? `${step.name} · ready for you` : status === 'skipped' ? `${step.name} skipped · now ${where}` : `${step.name} · now ${where}`) + (settled.length ? ` · then by event: ${settled.join('; ')}` : '');
  return { entry, words, was: `Was: ${step.name}${lead.stage ? ` · ${lead.stage.name}` : ''}` };
}

/** The tap put back: the lead as it was, only the rows that tap made removed. */
export async function undoTap(entry: string): Promise<void> {
  await rpc('pipeline_undo', { p_activity: entry });
  touched();
}

/**
 * Park (the stage and the step stay; the re-approach date is the next touch and is required), Lost (the reason is required;
 * next touch lands the setting's weeks out), Won (next touch and reply-by clear), Bring back (open: the lead is where it was),
 * or Not a lead. The engine's outcomeRefusal says no before the database would (Spencer, 2026-10-22); the database is the wall.
 */
export async function setOutcome(lead: Lead, outcome: 'open' | 'won' | 'lost' | 'parked' | 'not_a_lead', note?: string | null, nextTouch?: string | null, stage?: string | null): Promise<Tap> {
  const refusal = outcomeRefusal(outcome, { nextTouch, reason: note });
  if (refusal) {
    toast(refusal);
    throw new Error(refusal);
  }
  let entry: string;
  try {
    entry = await rpc<string>('pipeline_outcome', { p_opportunity: lead.id, p_outcome: outcome, p_note: note ?? null, p_next_touch: nextTouch ?? null, p_stage: stage ?? null });
  } catch (e) {
    toast(e instanceof Error ? e.message : String(e));
    throw e;
  }
  touched();
  const back = outcome === 'open' ? (lead.returnTo ? `Brought back · ${lead.returnTo.stage.name} · ${lead.returnTo.step.name}` : 'Brought back') : null;
  const words = outcome === 'parked' ? `Parked${nextTouch ? ` · re-approach ${nextTouch}` : ''}` : outcome === 'lost' ? `Lost${note ? ` · ${note}` : ''}` : outcome === 'won' ? 'Won' : outcome === 'open' ? back! : 'Not a lead';
  return { entry, words, was: `Was: ${lead.step?.name ?? lead.outcome}${lead.stage ? ` · ${lead.stage.name}` : ''}` };
}

/** A move on the stage track: the lead lands where the engine's landingStep says (the step it was on, back to a stage it stood on). */
export async function moveStage(lead: Lead, stage: PipelineStage, first: PipelineStep | null): Promise<Tap> {
  const entry = await rpc<string>('pipeline_move', { p_opportunity: lead.id, p_stage: stage.id, p_step: first?.id ?? null, p_note: null });
  if (lead.outcome !== 'open' && stage.kind === 'working') await rpc('pipeline_outcome', { p_opportunity: lead.id, p_outcome: 'open', p_note: null, p_next_touch: null, p_stage: stage.id });
  touched();
  await settleEvents(lead.id);
  return { entry, words: `${stage.name}${first ? ` · ${first.name}` : ''}`, was: `Was: ${lead.stage?.name ?? lead.outcome}${lead.step ? ` · ${lead.step.name}` : ''}` };
}

export async function handOff(lead: Lead, step: PipelineStep | null, toUser: string | null, asTask: boolean, note: string): Promise<void> {
  await rpc('pipeline_hand_off', { p_opportunity: lead.id, p_step: step?.id ?? null, p_to_user: toUser, p_as_task: asTask, p_note: note || null });
  touched();
}

export async function logTouch(oppId: string, entry: { kind: 'touch' | 'note'; channel?: string | null; direction?: 'in' | 'out' | null; body: string; contactId?: string | null; byUserId?: string | null }): Promise<void> {
  await insertRow('activity', { opportunity_id: oppId, kind: entry.kind, channel: entry.channel ?? null, direction: entry.direction ?? null, body: entry.body, contact_id: entry.contactId ?? null, by_user_id: entry.byUserId ?? null, source: 'app' });
  touched();
}

export async function splitOpportunity(lead: Lead, name: string, jobIds: string[], activityIds: string[]): Promise<string> {
  const id = await rpc<string>('split_opportunity', { p_opportunity: lead.id, p_name: name, p_job_ids: jobIds, p_activity_ids: activityIds });
  touched();
  return id;
}

export interface Placement {
  stage: string | null;
  step: string | null;
  first_visit: string | null;
  design_lane: string | null;
  anchor_type: string | null;
  anchor_date: string | null;
  anchor_confirmed: boolean;
  next_touch: string | null;
}

export async function placeLead(lead: Lead, pl: Placement): Promise<void> {
  await rpc('place_lead', {
    p_opportunity: lead.id,
    p_stage: pl.stage,
    p_step: pl.step,
    p_first_visit: pl.first_visit,
    p_design_lane: pl.design_lane,
    p_anchor_type: pl.anchor_type,
    p_anchor_date: pl.anchor_date,
    p_anchor_confirmed: pl.anchor_confirmed,
    p_next_touch: pl.next_touch,
  });
  touched();
  // placement included: an event step whose event is already true ticks at once ("Capture the lead" with a contact linked)
  if (pl.step) await settleEvents(lead.id);
}

/** A field of the opportunity typed on a screen (value, likelihood, anchor, reply-by, next touch, the answers). */
export async function saveLead(lead: Lead, patch: Row): Promise<void> {
  await updateRow('opportunity', lead.id, patch);
}

/**
 * An event step whose event is already true ticks as soon as the lead lands on it (decision 4, 2026-10-19), placement
 * included: after every tap, while the step on now is an event step the database can see is done (a contact linked for
 * "Capture the lead", a job's Proposal step done for "Estimator builds the Proposal"), tick it and move on. Called where a
 * lead moves (tickStep, placeLead, moveStage) and where the event happens (the job's Proposal step, Details.tsx). Reads
 * fresh rows, so it needs no hook. Returns the steps done this way, in words.
 */
export async function settleEvents(opportunityId: string, eventKey?: string): Promise<string[]> {
  const done: string[] = [];
  for (let i = 0; i < 6; i++) {
    const [opp] = await fetchRows('opportunity', { eq: { id: opportunityId } });
    if (!opp?.current_step_id) break;
    const steps = (await fetchRows('pipeline_step', { order: ['sort'] })) as PipelineStep[];
    const step = steps.find((x) => x.id === opp.current_step_id);
    if (!step || step.done_rule !== 'event' || !step.event_key) break;
    if (eventKey && done.length === 0 && step.event_key !== eventKey) break;
    const facts = await eventFacts(opportunityId, step);
    if (!eventIsTrue(step.event_key, facts)) break;
    const stages = (await fetchRows('pipeline_stage', { order: ['sort'] })) as PipelineStage[];
    const [cond] = await fetchRows('setting', { eq: { key: 'pipeline_conditions' } });
    const rows = (await fetchRows('opportunity_step', { eq: { opportunity_id: opportunityId } })) as OpportunityStepRow[];
    const next = nextStep(steps, stages, (cond?.value ?? []) as PipelineCondition[], opp, step);
    await rpc('pipeline_tick', {
      p_opportunity: opportunityId,
      p_step: step.id,
      p_status: 'done',
      p_next_step: next?.step.id ?? null,
      p_next_stage: next?.stage.id ?? null,
      p_round: rows.filter((r) => r.step_id === step.id && r.status !== 'ready').length + 1,
      p_review_link: null,
      p_note: 'by event',
      p_for_user: null,
      p_answer: null,
      p_outcome: next ? null : 'won',
    });
    done.push(`${step.name} · now ${next ? next.step.name : 'Won'}`);
  }
  if (done.length) touched();
  return done;
}

/** The events the database can see for one lead, read fresh. */
async function eventFacts(opportunityId: string, step: PipelineStep): Promise<{ hasContact: boolean; proposalDone: boolean; conceptBoardSent: boolean; intakeTextLogged: boolean; engineeringCheckDone: boolean; nextTouchArrived: boolean }> {
  const [links, opp] = await Promise.all([fetchRows('opportunity_contact', { eq: { opportunity_id: opportunityId } }), fetchRows('opportunity', { eq: { id: opportunityId } })]);
  // the intake text: a touch the CRM script marked as one (payload.touch_kind); no other outbound text counts (2026-10-22)
  const intake = step.event_key === 'intake_text_logged' ? (await fetchRows('activity', { eq: { opportunity_id: opportunityId, kind: 'touch' } })).some((a) => a.payload?.touch_kind === 'intake_text') : false;
  let proposalDone = false;
  if (step.event_key === 'job_proposal_done') {
    const [jobs, workflow] = await Promise.all([fetchRows('job', { eq: { opportunity_id: opportunityId } }), fetchRows('workflow_step', { order: ['sort'] })]);
    const proposal = workflow.find((s) => s.key === STEP.proposal);
    if (proposal && jobs.length) {
      const jobSteps = await fetchRows('job_step', { in: { job_id: jobs.map((j) => j.id) } });
      proposalDone =
        jobSteps.some((js) => js.workflow_step_id === proposal.id && js.actual_date) ||
        jobs.some((j) => {
          const st = workflow.find((s) => s.id === j.stage_id);
          return st && Number(st.sort) >= Number(proposal.sort);
        });
    }
  }
  const sent = step.event_key === 'concept_board_send' ? (await fetchRows('activity', { eq: { opportunity_id: opportunityId, kind: 'concept_board' } })).length > 0 : false;
  // every live job of the lead has its Engineering check done: the database works it out (ug.engineering_check_done; 2026-10-22)
  let engineeringCheckDone = false;
  if (step.event_key === 'job_engineering_check_done') {
    const { data } = await supabase.rpc('engineering_check_done', { p_opportunity: opportunityId });
    engineeringCheckDone = data === true;
  }
  // the one date event (2026-10-24): the lead's next touch date has arrived
  return { hasContact: links.length > 0 || !!opp[0]?.primary_contact_id, proposalDone, conceptBoardSent: sent, intakeTextLogged: intake, engineeringCheckDone, nextTouchArrived: nextTouchArrived(opp[0]?.next_touch_at ?? null) };
}

/** The one-event form the job's Details tab calls when its Proposal step is done. */
export async function tickByEvent(opportunityId: string, eventKey: string): Promise<boolean> {
  return (await settleEvents(opportunityId, eventKey)).length > 0;
}

/** "Lauren Schoeberl primary · James Schoeberl" */
export function contactWords(lead: Lead): string {
  return lead.contacts.map((c, i) => `${contactName(c)}${i === 0 && lead.contacts.length > 1 ? ' (primary)' : ''}`).join(' · ');
}
